import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, User } from 'lucide-react';
import '../pages/ArticlesPage/ArticlesPage.css';

function formatDate(dt) {
    if (!dt) return '';
    const raw = String(dt).trim();
    const withZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(raw)
        ? raw
        : `${raw.replace(' ', 'T')}Z`;
    return new Date(withZone).toLocaleDateString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
}

export function getArticleAuthors(article) {
    if (Array.isArray(article?.authors) && article.authors.length > 0) {
        return article.authors;
    }
    if (article?.author_id || article?.author_name) {
        return [{
            id: article.author_id,
            name: article.author_name,
            photo_url: article.author_photo
        }];
    }
    return [];
}

/**
 * Список статей с фильтром автора и поиском — как на /public/articles.
 */
const ArticlesSearchBlock = ({
    articles = [],
    authorOptions: authorOptionsProp,
    isLoading = false,
    className = ''
}) => {
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const [authorId, setAuthorId] = useState('');

    const authorOptions = useMemo(() => {
        if (Array.isArray(authorOptionsProp) && authorOptionsProp.length > 0) {
            return authorOptionsProp;
        }
        const map = new Map();
        articles.forEach((article) => {
            getArticleAuthors(article).forEach((author) => {
                if (author?.id != null && !map.has(author.id)) {
                    map.set(author.id, {
                        id: author.id,
                        name: author.name,
                        photo_url: author.photo_url
                    });
                }
            });
        });
        return [...map.values()].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'ru')
        );
    }, [authorOptionsProp, articles]);

    const filteredArticles = useMemo(() => {
        const q = String(query || '').trim().toLowerCase();
        return articles.filter((article) => {
            if (authorId) {
                const authors = getArticleAuthors(article);
                const matchAuthor = authors.some((a) => String(a.id) === String(authorId))
                    || String(article.author_id) === String(authorId);
                if (!matchAuthor) return false;
            }
            if (!q) return true;
            const title = String(article.title || '').toLowerCase();
            const excerpt = String(article.excerpt || '').toLowerCase();
            const content = stripHtml(article.content || '').toLowerCase();
            return title.includes(q) || excerpt.includes(q) || content.includes(q);
        });
    }, [articles, authorId, query]);

    const handleSearch = useCallback((e) => {
        e?.preventDefault?.();
        setQuery(searchInput.trim());
    }, [searchInput]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch(e);
    };

    const hasActiveFilters = Boolean(query || authorId);

    return (
        <div className={`articles-search-block ${className}`.trim()}>
            <div className="articles-search-bar">
                <select
                    className="articles-author-filter"
                    value={authorId}
                    onChange={(e) => setAuthorId(e.target.value)}
                    aria-label="Фильтр по автору"
                >
                    <option value="">Все авторы</option>
                    {authorOptions.map((author) => (
                        <option key={author.id} value={String(author.id)}>
                            {author.name}
                        </option>
                    ))}
                </select>

                <div className="articles-search-input-wrap">
                    <Search size={18} className="articles-search-icon" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Поиск по названию и содержимому статей..."
                        className="articles-search-input"
                    />
                    {searchInput && (
                        <button
                            className="articles-search-clear"
                            onClick={() => {
                                setSearchInput('');
                                setQuery('');
                            }}
                            type="button"
                        >
                            ×
                        </button>
                    )}
                </div>
                <button className="articles-search-btn" onClick={handleSearch} type="button">
                    Найти
                </button>
            </div>

            {hasActiveFilters && (
                <div className="articles-search-meta">
                    {`Найдено: ${filteredArticles.length}`}
                    {query ? ` по запросу «${query}»` : ''}
                    {authorId
                        ? ` · автор: ${authorOptions.find((a) => String(a.id) === String(authorId))?.name || ''}`
                        : ''}
                </div>
            )}

            {isLoading ? (
                <div className="articles-loading">
                    <div className="spinner" />
                </div>
            ) : filteredArticles.length === 0 ? (
                <div className="articles-empty">
                    {hasActiveFilters
                        ? 'По выбранным условиям статьи не найдены'
                        : 'Статьи пока не опубликованы'}
                </div>
            ) : (
                <div className="articles-search-list">
                    {filteredArticles.map((article) => {
                        const authors = getArticleAuthors(article);
                        const matchInContent = query && (() => {
                            const q = query.toLowerCase();
                            const title = String(article.title || '').toLowerCase();
                            const excerpt = String(article.excerpt || '').toLowerCase();
                            if (title.includes(q) || excerpt.includes(q)) return false;
                            return stripHtml(article.content || '').toLowerCase().includes(q);
                        })();

                        return (
                            <Link
                                key={article.id}
                                to={`/public/articles/${article.id}`}
                                className="articles-search-card"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="articles-search-card-content">
                                    <h2 className="articles-search-title">{article.title}</h2>
                                    {(article.excerpt || article.content) && (
                                        <p className="articles-search-excerpt">
                                            {stripHtml(article.excerpt || article.content).slice(0, 200)}
                                            {stripHtml(article.excerpt || article.content).length > 200 ? '...' : ''}
                                        </p>
                                    )}
                                </div>
                                <div className="articles-search-card-meta">
                                    <div className="articles-search-authors">
                                        {authors.map((author) => (
                                            <span
                                                key={author.id || author.name}
                                                className="articles-search-meta-item articles-search-author"
                                            >
                                                {author.photo_url ? (
                                                    <img
                                                        src={author.photo_url}
                                                        alt=""
                                                        className="articles-search-author-photo"
                                                    />
                                                ) : (
                                                    <span className="articles-search-author-placeholder">
                                                        <User size={12} />
                                                    </span>
                                                )}
                                                {author.name}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="articles-search-meta-item">
                                        <Clock size={14} />
                                        {formatDate(article.published_at || article.updated_at)}
                                    </span>
                                    {(article.match_in === 'content' || matchInContent) && query && (
                                        <span className="articles-match-badge">в тексте</span>
                                    )}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ArticlesSearchBlock;
